import { useEffect, useRef, useCallback } from "react";
import { Map as MapLibreMap } from "maplibre-gl";
import { bbox } from "@turf/turf";
import type { TaskGeometry } from "@/types/task";
import "maplibre-gl/dist/maplibre-gl.css";

interface GeometryPreviewMapProps {
  geometry?: TaskGeometry;
  height?: string;
}

export const GeometryPreviewMap: React.FC<GeometryPreviewMapProps> = ({
  geometry,
  height = "200px",
}) => {
  const mapInstanceRef = useRef<MapLibreMap | null>(null);
  const styleLoadedRef = useRef(false);
  const geometryRef = useRef(geometry);
  geometryRef.current = geometry;

  // Ref callback: initializes the map when the container mounts,
  // and cleans up when it unmounts. Survives StrictMode double-invoke.
  const containerRef = useCallback((node: HTMLDivElement | null) => {
    // Cleanup previous map if any
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
      styleLoadedRef.current = false;
    }

    if (!node) return;

    const map = new MapLibreMap({
      container: node,
      style: "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json",
      center: [-98.5795, 39.8283],
      zoom: 10,
      attributionControl: false,
    });

    mapInstanceRef.current = map;

    // Use both "load" and "style.load" events, plus a polling fallback.
    // MapLibre's "load" event may not fire reliably on non-interactive maps,
    // but the style becomes usable once getStyle() returns layers.
    const onReady = () => {
      if (styleLoadedRef.current) return;
      styleLoadedRef.current = true;
      addGeometry(map, geometryRef.current);
    };

    map.on("load", onReady);
    map.on("style.load", onReady);

    // Polling fallback: check every 200ms if style is ready
    const pollId = setInterval(() => {
      if (styleLoadedRef.current) {
        clearInterval(pollId);
        return;
      }
      try {
        const style = map.getStyle();
        if (style && style.layers && style.layers.length > 0) {
          clearInterval(pollId);
          onReady();
        }
      } catch {
        // Map may not be ready yet
      }
    }, 200);

    // Cleanup polling on map remove
    map.on("remove", () => clearInterval(pollId));
  }, []);

  // Update geometry when it changes (after map is loaded)
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !styleLoadedRef.current) return;
    addGeometry(map, geometry);
  }, [geometry]);

  return (
    <div className="relative border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
      <div ref={containerRef} style={{ height }} className="w-full" />
      {!geometry && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
          <div className="text-center text-gray-500">
            <div className="text-2xl mb-2">🗺️</div>
            <div className="text-sm">No geometry selected</div>
          </div>
        </div>
      )}
    </div>
  );
};

function addGeometry(map: MapLibreMap, geometry?: TaskGeometry) {
  // Remove existing layers/source
  try {
    if (map.getLayer("preview-geometry-fill"))
      map.removeLayer("preview-geometry-fill");
    if (map.getLayer("preview-geometry-line"))
      map.removeLayer("preview-geometry-line");
    if (map.getLayer("preview-geometry-point"))
      map.removeLayer("preview-geometry-point");
    if (map.getSource("preview-geometry"))
      map.removeSource("preview-geometry");
  } catch {
    // Layers may not exist yet
  }

  if (!geometry) return;

  try {
    const geojson: GeoJSON.FeatureCollection = {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          geometry: geometry,
          properties: {},
        },
      ],
    };

    map.addSource("preview-geometry", {
      type: "geojson",
      data: geojson,
    });

    if (geometry.type === "Polygon" || geometry.type === "MultiPolygon") {
      map.addLayer({
        id: "preview-geometry-fill",
        type: "fill",
        source: "preview-geometry",
        paint: { "fill-color": "#374795", "fill-opacity": 0.4 },
      });
      map.addLayer({
        id: "preview-geometry-line",
        type: "line",
        source: "preview-geometry",
        paint: { "line-color": "#374795", "line-width": 2, "line-opacity": 1 },
      });
    } else if (
      geometry.type === "LineString" ||
      geometry.type === "MultiLineString"
    ) {
      map.addLayer({
        id: "preview-geometry-line",
        type: "line",
        source: "preview-geometry",
        paint: { "line-color": "#374795", "line-width": 4, "line-opacity": 1 },
      });
    } else if (geometry.type === "Point" || geometry.type === "MultiPoint") {
      map.addLayer({
        id: "preview-geometry-point",
        type: "circle",
        source: "preview-geometry",
        paint: {
          "circle-radius": 8,
          "circle-color": "#2563eb",
          "circle-stroke-color": "#2563eb",
          "circle-stroke-width": 3,
          "circle-opacity": 0.7,
        },
      });
    }

    // Fit map to geometry bounds
    try {
      const [minLng, minLat, maxLng, maxLat] = bbox(geojson);
      map.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        { padding: 20, duration: 0 }
      );
      if (geometry.type === "Point") {
        map.setZoom(16.5);
      }
    } catch (error) {
      console.warn("Error fitting bounds:", error);
    }
  } catch (error) {
    console.error("Error rendering geometry on preview map:", error);
  }
}
